'use client'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { ArrowDownToLine, ArrowUpFromLine, Trash2 } from 'lucide-react'

import { AddCardButton } from '~/app/accounts/cards/AddCardButton'
import { useRemoveCardDialog } from '~/app/accounts/cards/useRemoveCardDialog'
import { FeatureEmptyState } from '~/components/FeatureEmptyState'
import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import {
  AddressDisplay,
  CurrencyAmount,
  Dropdown,
  DropdownEntry,
  LoadingSpinner,
} from '~/components/component-library'
import { RAIN_DASHBOARD_URL, RAIN_DOCS_URL } from '~/constants/externalUrls'
import { useCardBalances } from '~/hooks/useCardBalances'
import { useCards } from '~/hooks/useCards'
import { Card } from '~/types/cards'
import { buildSendPath } from '~/utils/send'

const ADDRESS_COLUMN_WIDTH = 320
const columnHelper = createColumnHelper<Card>()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CardColumn = ColumnDef<Card, any>

const CardBalanceCell = ({ card }: { card: Card }) => {
  const { totalValue, isLoading } = useCardBalances(card.address)

  return (
    <div className="flex w-full items-center justify-end">
      <CurrencyAmount
        value={totalValue}
        isLoading={isLoading}
        layout="inline"
      />
    </div>
  )
}

const CardActionsCell = ({ card }: { card: Card }) => {
  const openRemoveCardDialog = useRemoveCardDialog()

  const items: DropdownEntry[] = [
    {
      icon: <ArrowDownToLine />,
      content: 'Deposit',
      href: { path: buildSendPath(card.address) },
    },
    {
      icon: <ArrowUpFromLine />,
      content: 'Withdraw',
      href: { path: RAIN_DASHBOARD_URL, newTab: true },
    },
    'divider',
    {
      icon: <Trash2 />,
      content: 'Remove',
      onClick: () => openRemoveCardDialog(card),
    },
  ]

  return (
    <div className="flex justify-end">
      <Dropdown items={items}>
        <TableActionTrigger aria-label="Card actions" />
      </Dropdown>
    </div>
  )
}

const columns: CardColumn[] = [
  columnHelper.accessor('address', {
    header: 'Address',
    size: ADDRESS_COLUMN_WIDTH,
    maxSize: ADDRESS_COLUMN_WIDTH,
    cell: function AddressCell(ctx) {
      return (
        <AddressDisplay
          address={ctx.row.original.address}
          layout="two-line"
          isInternal
        />
      )
    },
    enableColumnFilter: false,
  }),
  columnHelper.display({
    id: 'balance',
    header: 'Balance',
    meta: { align: 'right' },
    cell: function BalanceCell(ctx) {
      return <CardBalanceCell card={ctx.row.original} />
    },
    enableColumnFilter: false,
  }),
  columnHelper.display({
    id: 'actions',
    ...TABLE_ACTION_COLUMN_WIDTH,
    cell: function ActionsCell(ctx) {
      return <CardActionsCell card={ctx.row.original} />
    },
    meta: TABLE_ACTION_COLUMN_META,
    enableColumnFilter: false,
  }),
]

// Renders inside the accounts page's Cards tab, which handles auth and the
// page chrome.
export const CardsPage = () => {
  const { cards, isLoading } = useCards()

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-8">
        <LoadingSpinner size="xl" thickness="normal" variant="default" />
      </div>
    )
  }

  if (cards.length === 0) {
    return (
      <FeatureEmptyState
        title="Cards"
        showTitle={false}
        paragraphs={[
          'Already have a Rain cards account? Add your collateral address to monitor balance and spend, and top it up directly in Splits.',
        ]}
        learnMoreUrl={RAIN_DOCS_URL}
        createButton={<AddCardButton />}
      />
    )
  }

  return (
    <Table
      columns={columns}
      data={cards}
      isLoading={isLoading}
      rowPath={(row) => `/accounts/cards/${row.original.address}`}
    />
  )
}
